/**
 * Activity: what the other person (or the agent) did, from the server's change log — which
 * kinds there are, how each one reaches you (a push, just the Activity list, or not at all),
 * and the defaults. Shared by the push digests (worker) and the Activity page (web).
 */
import { addDays, dateInTz } from './dates'

export type ActivityCat = 'added' | 'assigned' | 'soon' | 'dates' | 'done' | 'deleted' | 'edited'
export type ActivityLevel = 'push' | 'list' | 'off'
/** The filter groups on the Activity page. */
export type ActivityGroup = 'added' | 'changed' | 'done' | 'deleted'

export const ACTIVITY_CATS: readonly ActivityCat[] = ['added', 'assigned', 'soon', 'dates', 'done', 'deleted', 'edited']

export const ACTIVITY_DEFAULTS: Record<ActivityCat, ActivityLevel> = {
  added: 'push',
  assigned: 'push',
  soon: 'push',
  dates: 'list',
  done: 'list',
  deleted: 'list',
  edited: 'off',
}

export const ACTIVITY_LABELS: Record<ActivityCat, { label: string; hint?: string }> = {
  added: { label: 'Added something', hint: 'A task, event or note' },
  assigned: { label: 'Assigned you something' },
  soon: { label: 'Put something on today or tomorrow' },
  dates: { label: 'Changed a date', hint: 'Further out, or took one off' },
  done: { label: 'Finished something of yours', hint: 'One you made, or that’s assigned to you' },
  deleted: { label: 'Deleted something', hint: 'With Undo' },
  edited: { label: 'Renamed or moved something' },
}

/** How many days the Activity page goes back. */
export const ACTIVITY_DAYS = 7

export function activityGroup(cat: ActivityCat): ActivityGroup {
  return cat === 'added' ? 'added' : cat === 'done' ? 'done' : cat === 'deleted' ? 'deleted' : 'changed'
}

/** A person's levels: their choices (user setting `activity`) over the defaults. */
export function activityLevels(settings: Record<string, unknown>): Record<ActivityCat, ActivityLevel> {
  const raw = settings.activity
  const out = { ...ACTIVITY_DEFAULTS }
  if (raw && typeof raw === 'object')
    for (const c of ACTIVITY_CATS) {
      const v = (raw as Record<string, unknown>)[c]
      if (v === 'push' || v === 'list' || v === 'off') out[c] = v
    }
  return out
}

export interface ChangeLike {
  kind: string
  summary: string | null
  actor: string | null
  at: number
}

/**
 * Which kind of activity a change-log entry is for `viewer` — or null when it isn't theirs to
 * hear about (their own changes, housekeeping, other people's finished things).
 */
export function classifyChange(c: ChangeLike, viewer: string, item: { assignee?: string | null; createdBy?: string | null } | undefined, tz: string): ActivityCat | null {
  if (!c.actor || c.actor === viewer || c.actor === 'system') return null
  switch (c.kind) {
    case 'created':
      return 'added'
    case 'assigned':
      return c.summary === viewer ? 'assigned' : 'edited'
    case 'scheduled': {
      if (!c.summary || c.summary === 'unscheduled') return 'dates'
      const day = dateInTz(c.at, tz)
      return c.summary === day || c.summary === addDays(day, 1) ? 'soon' : 'dates'
    }
    case 'completed':
      return item && (item.assignee === viewer || item.createdBy === viewer) ? 'done' : null
    case 'trashed':
      return 'deleted'
    case 'renamed':
    case 'moved':
    case 'reopened':
    case 'restored':
    case 'archived':
    case 'unarchived':
      return 'edited'
    default:
      return null
  }
}
